/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import React from 'react';
import { connect } from 'react-redux';
import {
  formValueSelector, change, unregisterField
} from 'redux-form';
import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  TextField, LookupField, CheckboxField, RadioGroupField, DecimalField, GroupField
} from 'react/components/fields';
import { validIfServerVersionIsBiggerThan, validIfBuildNumberIsBiggerThan } from 'react/utils/utils';
const RESTRICT_ALLOWED_QUANTITY = {
  noRestrict: 'noRestrict',
  restrictBySize: 'restrictBySize',
  restrictBySizeAndSkuType: 'restrictBySizeAndSkuType'
};

const renderGeneralSection = () => (
  <FormSection title="OrderClass.section.general.title">
    <FormSectionColumn>
      <TextField label="OrderClass.field.key" name="key" disabledOnEdit required />
      <LookupField
        label="OrderClass.field.organization"
        name="organization"
        lookupEntity="Organization"
        lookupEntityColumn="description"
        disabledOnEdit
        required
      />
      <TextField label="OrderClass.field.description" name="description" required />
      <CheckboxField label="OrderClass.field.enabled" name="enabled" />
    </FormSectionColumn>
  </FormSection>
);

const renderConfigurationSection = ({ changeFieldValuesAction }) => (
  <FormSection title="OrderClass.section.configuration.title">
    <FormSectionColumn>
      <CheckboxField
        label="OrderClass.field.pickup"
        name="pickup"
        hint="OrderClass.field.pickup.hint"
        onChange={(checked) => {
          if (!checked) {
            changeFieldValuesAction('OrderClass', 'useConsignedSku', false);
          }
        }}
      />
    </FormSectionColumn>
    <FormSectionColumn>
      <CheckboxField label="OrderClass.field.allDamaged" name="allDamaged" />
    </FormSectionColumn>
  </FormSection>
);

const renderAllowedLocationTypesSection = () => (
  <FormSection
    title="OrderClass.section.AllowedLocationTypes.title"
    subtitle="OrderClass.section.AllowedLocationTypes.subtitle"
  >
    <FormSectionColumn>
      <LookupField
        label="OrderClass.field.locationType"
        name="allowedLocationTypes"
        lookupEntity="LocationType"
        lookupEntityColumn="description"
        monetMeta={{ hasMany: { entityName: 'locationType' } }}
        multiple
      />
    </FormSectionColumn>
  </FormSection>
);

const renderContainerDecimalFields = () => (
  <GroupField
    name="allowedOrderSizes"
    validate={(value) => {
      if (value.length && !value[0].size1 && !value[0].size2 && !value[0].size3) {
        return new Map().set(0, Monet.I18n.getResourcesByKey('OrderClass.error.allowedOrderSizes'));
      }
      return undefined;
    }}
    showOnlyOneItem
    requiredOnce
  >
    <DecimalField label="OrderClass.field.allowedSize1" name="size1" />
    <DecimalField label="OrderClass.field.allowedSize2" name="size2" />
    <DecimalField label="OrderClass.field.allowedSize3" name="size3" />
  </GroupField>
);

const filterSkuTypeDuplicated = (rows, skuTypeId, index) => rows.filter(
  (v, i) => i !== index && v?.skuType && v.skuType.id === skuTypeId
).length;

const renderContainerDecimalFieldsAndSkuTypes = ({ allowedOrderSizes }) => (
  <GroupField
    name="allowedOrderSizes"
    requiredOnce
    validate={(values) => {
      const errorObject = new Map();
      values.forEach((value, index) => {
        if (!value?.skuType?.id) {
          errorObject.set(index, Monet.I18n.getResourcesByKey('OrderClass.error.allowedOrderSizesSkuType'));
        } else if (index > 0
          && value?.skuType?.id && filterSkuTypeDuplicated(values, value.skuType.id, index)) {
          errorObject.set(index, Monet.I18n.getResourcesByKey('OrderClass.error.shouldNotRepeatSkuType'));
        } else if (!value.size1 && !value.size2 && !value.size3) {
          errorObject.set(index, Monet.I18n.getResourcesByKey('OrderClass.error.allowedOrderSizes'));
        }
      });
      if (errorObject.size) {
        return errorObject;
      }
      return undefined;
    }}
    onRemoveItemAction={(rows, index) => {
      const item = allowedOrderSizes.filter(
        (_, allowedOrderIndex) => allowedOrderIndex === index
      )[0];
      if (!item?.id) {
        rows.remove(index);
      } else if (confirm(Monet.I18n.getResourcesByKey('OrderClass.confirmRemoveSkuTypeAndDecimalRestrictionItem'))) {
        const ajax = new Monet.Ajax();
        ajax.url = '/AllowedOrderSize/BatchDelete';
        ajax.method = 'POST';
        ajax.data = [item.id];
        ajax.success = () => {
          rows.remove(index);
        };
        ajax.send();
      }
    }}
  >
    <LookupField
      label="OrderClass.field.singleSkuType"
      name="skuType"
      lookupEntity="SkuType"
      lookupEntityColumn="description"
      monetMeta={{ hasMany: { entityName: 'skuType' } }}
    />
    <DecimalField label="OrderClass.field.allowedSize1" name="size1" />
    <DecimalField label="OrderClass.field.allowedSize2" name="size2" />
    <DecimalField label="OrderClass.field.allowedSize3" name="size3" />
  </GroupField>
);

const changeAllowedOrderSizesValue = (value, changeFieldValuesAction, unregisterFieldAction) => {
  switch (value) {
    case RESTRICT_ALLOWED_QUANTITY.restrictBySize:
      changeFieldValuesAction('OrderClass', 'allowedOrderSizes', [{ size1: 0, size2: 0, size3: 0 }]);
      break;
    case RESTRICT_ALLOWED_QUANTITY.restrictBySizeAndSkuType:
      changeFieldValuesAction('OrderClass', 'allowedOrderSizes', [{
        skuType: undefined, size1: 0, size2: 0, size3: 0
      }]);
      break;
    default:
      unregisterFieldAction('OrderClass', 'allowedOrderSizes');
      changeFieldValuesAction('OrderClass', 'allowedOrderSizes', []);
      break;
  }
  changeFieldValuesAction('OrderClass', 'restrictAllowedQuantity', value);
};

const onRestrictionAllowedQuantityChange = ({
  value,
  changeFieldValuesAction,
  unregisterFieldAction,
  allowedOrderSizes
}) => {
  const savedValues = allowedOrderSizes?.filter((item) => item.id);
  const needConfirmation = (savedValues || []).length;

  let confirmed = false;

  if (needConfirmation) {
    confirmed = confirm(Monet.I18n.getResourcesByKey('OrderClass.confirmChangeRestrictAllowedQuantity'));
  }

  if (!needConfirmation && !confirmed) {
    changeAllowedOrderSizesValue(value, changeFieldValuesAction, unregisterFieldAction);
  } else if (confirmed) {
    const ajax = new Monet.Ajax();

    ajax.url = '/AllowedOrderSize/BatchDelete';
    ajax.method = 'POST';
    ajax.data = savedValues.map((item) => item.id);
    ajax.success = () => {
      changeAllowedOrderSizesValue(value, changeFieldValuesAction, unregisterFieldAction);
    };
    ajax.send();
  }
};

const renderNewOrderConfigurationSection = ({
  restrictAllowedQuantity,
  pickup,
  allowNewOrder,
  useConsignedSku,
  changeFieldValuesAction,
  allowedOrderSizes,
  unregisterFieldAction
}) => {
  let restrictAllowedQuantityValue = RESTRICT_ALLOWED_QUANTITY.noRestrict;
  if (
    (allowedOrderSizes?.length && allowedOrderSizes[0].skuType)
    || restrictAllowedQuantity === RESTRICT_ALLOWED_QUANTITY.restrictBySizeAndSkuType
  ) {
    restrictAllowedQuantityValue = RESTRICT_ALLOWED_QUANTITY.restrictBySizeAndSkuType;
  } else if (allowedOrderSizes?.length === 1
    || restrictAllowedQuantity === RESTRICT_ALLOWED_QUANTITY.restrictBySize) {
    restrictAllowedQuantityValue = RESTRICT_ALLOWED_QUANTITY.restrictBySize;
  }

  return (
    <FormSection title="OrderClass.section.NewOrderConfiguration.title">
      <FormSectionColumn>
        {validIfServerVersionIsBiggerThan(6.57) && validIfBuildNumberIsBiggerThan(1036467) && <CheckboxField
          label="OrderClass.field.mandatoryPaymentMethod"
          name="mandatoryPaymentMethod"
        />}
        <CheckboxField label="OrderClass.field.allowNewOrder" name="allowNewOrder" />
        <LookupField
          label="OrderClass.field.skuType"
          name="allowedSkuTypes"
          lookupEntity="SkuType"
          lookupEntityColumn="description"
          monetMeta={{ hasMany: { entityName: 'skuType' } }}
          disabled={!allowNewOrder || useConsignedSku}
          multiple
        />
      </FormSectionColumn>
      <FormSectionColumn>
        <CheckboxField
          label="OrderClass.field.useConsignedSku"
          hint="OrderClass.field.useConsignedSku.hint"
          name="useConsignedSku"
          disabled={!pickup}
        />
      </FormSectionColumn>
      <FormSectionColumn className="full-section">
        <RadioGroupField
          label="OrderClass.field.restrictAllowedQuantity"
          name="restrictAllowedQuantity"
          onChange={value => onRestrictionAllowedQuantityChange({
            value,
            changeFieldValuesAction,
            unregisterFieldAction,
            allowedOrderSizes
          })}
          defaultValue={restrictAllowedQuantityValue}
          value={restrictAllowedQuantityValue}
          disabled={!allowNewOrder || useConsignedSku}
          options={[
            {
              label: 'OrderClass.option.noRestrict',
              value: RESTRICT_ALLOWED_QUANTITY.noRestrict
            },
            {
              label: 'OrderClass.option.restrictBySize',
              value: RESTRICT_ALLOWED_QUANTITY.restrictBySize
            },
            {
              label: 'OrderClass.option.restrictBySizeAndSkuType',
              value: RESTRICT_ALLOWED_QUANTITY.restrictBySizeAndSkuType
            }
          ]}
        />
        {allowNewOrder && !useConsignedSku && (
          <>
            {restrictAllowedQuantity === 'restrictBySize' && renderContainerDecimalFields()}
            {(
              restrictAllowedQuantity === 'restrictBySizeAndSkuType'
              && renderContainerDecimalFieldsAndSkuTypes({
                allowedOrderSizes
              })
            )}
          </>
        )}
      </FormSectionColumn>
    </FormSection>
  );
};

const beforeSubmit = (values) => {
  const {
    allowedLocationTypes, allowedSkuTypes, organization, allowedOrderSizes
  } = values;
  const {
    Session: {
      Configurations: {
        size1AliasConfig, size2AliasConfig, size3AliasConfig
      }
    }
  } = Monet;
  const allowedOrderSizesMapped = allowedOrderSizes?.length
    ? allowedOrderSizes.map((allowedOrderSize) => {
      const data = allowedOrderSize;

      if (data.size1 && size1AliasConfig?.id) {
        data.size1Alias = { id: size1AliasConfig.id };
      }

      if (data.size2 && size2AliasConfig?.id) {
        data.size2Alias = { id: size2AliasConfig.id };
      }

      if (data.size3 && size3AliasConfig?.id) {
        data.size3Alias = { id: size3AliasConfig.id };
      }
      data.organization = values.organization;
      return data;
    })
    : [];
  return {
    ...values,
    allowedOrderSizes: allowedOrderSizesMapped,
    allowedLocationTypes: allowedLocationTypes?.map(
      (oldValues) => ({ ...oldValues, organization })
    ) || [],
    allowedSkuTypes: allowedSkuTypes?.map((oldValues) => ({ ...oldValues, organization })) || []
  };
};

const OrderClassCrud = (props) => (
  <BaseCrud
    tabName="OrderClass.title"
    beforeSubmit={beforeSubmit}
    customCriteria={[
      'allowedOrderSizes.id',
      'allowedOrderSizes.skuType.id',
      'allowedOrderSizes.size1',
      'allowedOrderSizes.size2',
      'allowedOrderSizes.size3',
      'mandatoryPaymentMethod',
    ]}
    {...props}
  >
    {renderGeneralSection()}
    {renderConfigurationSection(props)}
    {renderNewOrderConfigurationSection(props)}
    {renderAllowedLocationTypesSection()}
  </BaseCrud>
);

const valueSelector = formValueSelector('OrderClass');

const mapDispatchToProps = {
  changeFieldValuesAction: change,
  unregisterFieldAction: unregisterField
};

const mapStateToProps = (state) => ({
  allowNewOrder: valueSelector(state, 'allowNewOrder'),
  useConsignedSku: valueSelector(state, 'useConsignedSku'),
  pickup: valueSelector(state, 'pickup'),
  restrictAllowedQuantity: valueSelector(state, 'restrictAllowedQuantity'),
  allowedOrderSizes: valueSelector(state, 'allowedOrderSizes')
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderClassCrud);
